// bm-design-system: navigation tabs primitive
//
// A segmented row of links for switching between sibling pages of one section
// (e.g. Audits -> All audits / Intake questions).
//
// These are NAVIGATION tabs, not panel tabs: each one is a real URL, so the
// browser's back button, refresh and "open in new tab" all behave. Anything that
// swaps content in place without changing the URL wants a different component.
import * as React from "react";
import { Link } from "@inertiajs/react";
import { cn } from "@/lib/utils";

export interface NavTab {
  href: string;
  label: string;
  /** Decides whether this tab is active. Receives the FULL current url,
   *  query string included, so a set of tabs distinguished only by a query
   *  param can match on it. Defaults to an exact match of href against the
   *  path alone, which is what a set of sibling pages needs. */
  match?: (url: string) => boolean;
}

export interface NavTabsProps extends React.HTMLAttributes<HTMLElement> {
  tabs: NavTab[];
  /** The current path. Pass usePage().url so the component stays pure. */
  current: string;
  label?: string;
}

function isActive(tab: NavTab, current: string): boolean {
  // A custom matcher sees the whole url, so tabs that differ only by a query
  // param can tell themselves apart. The DEFAULT compares paths only, so an
  // unrelated ?range=30 cannot deactivate the tab it belongs to.
  if (tab.match) return tab.match(current);
  return current.split("?")[0] === tab.href;
}

function NavTabs({ tabs, current, label = "Section", className, ...props }: NavTabsProps) {
  return (
    <nav
      aria-label={label}
      className={cn("flex gap-1 border-b border-hairline", className)}
      {...props}
    >
      {tabs.map((tab) => {
        const active = isActive(tab, current);
        return (
          <Link
            key={tab.href}
            href={tab.href}
            aria-current={active ? "page" : undefined}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium no-underline transition-colors",
              active
                ? "border-accent text-ink-display"
                : "border-transparent text-ink-muted hover:text-ink-body",
            )}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
NavTabs.displayName = "NavTabs";

export { NavTabs };
